import AlertCircleIcon from '@assets/icons/alert.svg';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import BottomSheet from '@gorhom/bottom-sheet';
import HamburgerIcon from '@assets/icons/hamburger.svg';
import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import TranslationIcon from '@assets/icons/translation.svg';
import { adjustMomentForTimezone } from '@/utils/date';
import { AffordanceHandleIndicatorWithSpacing } from '@/components/bottom-sheet-backdrop';
import { Analytics } from '@/utils/analytics';
import {
  AssignSmartOrderFunction,
  useOrderInboundsDetail,
} from '@/hooks/use-order-inbounds-detail';
import { Button, SIZES } from '@/components/button';
import { CircledIcon } from '@/components/circled-icon';
import {
  CorrectionPreviewMap,
  OrderAgentWorkflowMode,
  OrderMetadata,
  SwapSuggestionMap,
  VariantPackOrderCountsMap,
} from './types';
import { createStyles } from '@/contexts/theme-context';
import {
  DRAWER_HEADER_HEIGHT,
  FONTS,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  SCREEN_PADDING_XS,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { FulfillmentName } from '@/types';
import { hasTranslations } from './utils';
import { ICON_BUTTON_HIT_SLOP } from '@/components/navigation-header-content';
import { InboundDetailForm } from './inbound-detail-form';
import { InboundDetailHeader } from './inbound-detail-header';
import { InboundDetailListCustomerAccountPicker } from './inbound-detail-list-customer-account-picker';
import { InboundDetailSplitContainer } from './inbound-detail-split-container';
import { InboundListExpandButton } from './inbound-list-expand-button';
import { InboundUnknownContact } from './inbound-unknown-contact';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
} from '@/contexts/layout-context';
import { ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import { OrderAvailability } from '@domain/fulfillment-settings/blocs/order-availability';
import {
  OrderInboundDetailParams,
  OrderInboundsStackParams,
} from '@/navigators/types';
import { OrderInboundsDetailActionSheet } from './order-inbounds-detail-action-sheet';
import { OrderInboundsDetailTabs } from './order-inbounds-detail-tabs/order-inbounds-detail-tabs';
import {
  Platform,
  StyleProp,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { RegenerateSmartOrders } from '@/hooks/use-regenerate-smart-order';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import { StackNavigationProp } from '@react-navigation/stack';
import { Toast, Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useNewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSmartOrderUnreads } from '@/hooks/use-smart-order-unreads';
import { useStatusBarColor } from '@/hooks/use-status-bar-color';
import {
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useThreadOrderEventsContext } from './thread-order-events-context';
import { useUniqueToast } from '@/hooks/use-unique-toasts';
import type { InboundDetailListOrderOptionsRef } from './inbound-detail-list-order-options';
import type { Props as InboundDetailSplitContainerProps } from './inbound-detail-split-container';

const snapPoints = ['50%', '85%'];

const BOTTOM_SHEET_BORDER_RADIUS = 16;
const CONTINUE_BUTTON_HEIGHT =
  SIZES.medium + SCREEN_PADDING + SCREEN_PADDING_SMALL;

const FOOTER_ACTIONS_HIDE_HEIGHT = 200;
const FOOTER_ACTIONS_HIDE_THRESHOLD = 15;
const FOOTER_ACTIONS_SHOW_THRESHOLD = 10;

const FOOTER_SLIDE_UP_ANIMATION_SPEED = 250;
const FOOTER_SLIDE_DOWN_ANIMATION_SPEED = 400;

export type Props = Omit<
  OrderInboundDetailParams,
  | 'initialRestaurantName'
  | 'initialRestaurantUUID'
  | 'initialSupplierUUID'
  | 'initialProcessing'
  | 'onClose'
> & {
  activeRestaurant: EmployeeRestaurant | null;
  alternativeVariantPacksMap: SwapSuggestionMap;
  assignSmartOrder: AssignSmartOrderFunction;
  children?: React.ReactNode;
  correctionPreviewMap: CorrectionPreviewMap;
  isInboundsListExpanded: boolean;
  isProcessing: boolean;
  isProcessingReply?: boolean;
  onRegenerateSmartOrder?: (orderUUID: string, restaurantUUID: string) => void;
  onResize?: InboundDetailSplitContainerProps['onResize'];
  orderMetadata: OrderMetadata[];
  previousVariantPacksMap: SwapSuggestionMap;
  refetchOrderDetails: () => void;
  refetchSearchTermCorrections: () => void;
  regenerateSmartOrders: RegenerateSmartOrders;
  rehydrateKey?: number;
  savedVariantPackOrderCountsMap: VariantPackOrderCountsMap;
  searchInputSubstitutionsMap: SwapSuggestionMap;
  selectNextSmartOrder: (smartOrderUUID: string) => void;
  setIsInboundsListExpanded: (isInboundsListExpanded: boolean) => void;
  setOrderInboundsListDrawerOpen?: (isOpen: boolean) => void;
  showItemListView: boolean;
  workflowMode: OrderAgentWorkflowMode;
};

export const OrderInboundsDetail: React.FC<Props> = ({
  activeRestaurant,
  alternativeVariantPacksMap,
  assignSmartOrder,
  children,
  correctionPreviewMap,
  isInboundsListExpanded,
  isProcessingReply,
  rehydrateKey,
  isProcessing,
  onRegenerateSmartOrder,
  onResize,
  orderMetadata,
  orderUUID,
  previousVariantPacksMap,
  refetchOrderDetails,
  refetchSearchTermCorrections,
  regenerateSmartOrders,
  savedVariantPackOrderCountsMap,
  searchInputSubstitutionsMap,
  selectNextSmartOrder,
  setIsInboundsListExpanded,
  setOrderInboundsListDrawerOpen,
  showItemListView,
  unknownContactInfo,
  workflowMode,
}) => {
  const navigation =
    useNavigation<
      StackNavigationProp<
        OrderInboundsStackParams,
        ORDER_INBOUNDS_ROUTES.Submit
      >
    >();

  // Create ref for order options component
  const orderOptionsRef = useRef<InboundDetailListOrderOptionsRef>(null);

  // STYLES //
  const [styles, theme] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();
  const marginTop = windowDimensions.height * 0.08 + top; // Top margin is 8% of screen height + the safe inset
  const maxHeight = windowDimensions.height * 0.85 - 52;
  const baseStyle = useLayoutSizeValue<StyleProp<ViewStyle>>(
    {
      [LayoutSize.SMALL]: null,
      [LayoutSize.SMALL_TABLET]: styles.baseTablet,
      [LayoutSize.MEDIUM]: null,
      [LayoutSize.LARGE]: null,
    },
    true,
  );
  const listContentContainerSmallStyle =
    Platform.OS === 'web'
      ? styles.listContentContainerSmallWeb
      : styles.listContentContainerSmall;

  // MISC //
  const showUniqueToast = useUniqueToast();
  const { trainingModeEnabled, isViewOnly } = useOrderInbounds();
  useStatusBarColor(theme.sk.color.bg.secondary.default);

  const isCorrectionMode = workflowMode === OrderAgentWorkflowMode.Correction;

  // DOMAIN HOOK //
  // Handlers, setters (and the values they set), and some computed values for this domain
  const {
    addItemSmartOrderUUID,
    continueButtonLoading,
    detailListItems,
    dispatch,
    getAddToOrderDisabled,
    isMemorizingOrder,
    navigateToAdd,
    onContinuePress,
    onItemCorrection,
    onRemoveItem,
    onSelectLocation,
    onSwapItem,
    onSetRememberSelection,
    onUnknownContactSave,
    onUpdateMatchesPress,
    orderInboundState,
    orderUUIDs,
    pillNavProps,
    recordInteraction,
    setAssigningRestaurant,
    sheetRef,
    showLocationPicker,
  } = useOrderInboundsDetail({
    activeRestaurant,
    assignSmartOrder,
    isProcessing,
    isViewOnly,
    navigation,
    onRegenerateSmartOrder,
    orderMetadata,
    orderOptionsRef,
    orderUUID,
    refetchOrderDetails,
    refetchSearchTermCorrections,
    regenerateSmartOrders,
    savedVariantPackOrderCountsMap,
    selectNextSmartOrder,
    unknownContactInfo,
  });

  const itemToSwap = useOrderInboundDetailStore((state) => state.itemToSwap);
  const addItemSheetOpen = useOrderInboundDetailStore(
    (state) => state.showAddItemSheet,
  );

  const setShowAddItemSheet = useOrderInboundDetailStore(
    (state) => state.actions.setShowAddItemSheet,
  );

  const setUpdatingLocation = useOrderInboundDetailStore(
    (state) => state.actions.setUpdatingLocation,
  );

  const selectedInputUUID = useOrderInboundDetailStore(
    (state) => state.selectedInputUUID,
  );

  const setSelectedInputUUID = useOrderInboundDetailStore(
    (state) => state.actions.setSelectedInputUUID,
  );

  // CONTEXTS //
  const { isSmallLayout, isSmallTabletLayout, isLargeLayout, isMediumLayout } =
    useLayoutContext();
  const { setNextFulfillment, selectedFulfillment, fulfillmentHydrated } =
    useNewOrderActionsContext();
  const { isUpdatingQuantity } = useNewOrderLoadingContext();
  const {
    hasResponse,
    restaurantUUID: contextRestaurantUUID,
    isLoading,
    availableOrderFulfillments,
    supplierTimezone,
  } = useNewOrderContext();
  const { selectedRestaurantRelationship, supplierEmployeeRole } =
    useAuthenticatedUserContext();

  const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
    'additional_order_validation_supplier_roles',
  );
  const hideMobileButtons =
    (isMediumLayout || isSmallTabletLayout) && (itemToSwap || addItemSheetOpen);

  const { markSmartOrdersAsRead } = useSmartOrderUnreads();
  const isUnknownContact = Boolean(unknownContactInfo);

  // LOCAL STATE //
  const [currentSnapPointIndex, setCurrentSnapPointIndex] = useState(0);
  const selectedFulfillmentRef = useRef<SelectedFulfillmentType>(null);
  const markedAsReadRef = useRef(false);
  const [showTranslation, setShowTranslation] = useState(false);

  const [scrollDelta, setScrollDelta] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const footerVisible = useSharedValue(1);

  // Reset footer visibility when itemToSwap changes
  useEffect(() => {
    if (!itemToSwap) {
      setScrollDelta({ x: 0, y: 0 });
      footerVisible.value = 1;
    }
  }, [itemToSwap, footerVisible]);

  useEffect(() => {
    if (scrollDelta.y >= FOOTER_ACTIONS_HIDE_THRESHOLD) {
      footerVisible.value = withTiming(0, {
        duration: FOOTER_SLIDE_DOWN_ANIMATION_SPEED,
        easing: Easing.linear,
      });
    } else if (scrollDelta.y <= -FOOTER_ACTIONS_SHOW_THRESHOLD) {
      footerVisible.value = withTiming(1, {
        duration: FOOTER_SLIDE_UP_ANIMATION_SPEED,
        easing: Easing.linear,
      });
    }
  }, [scrollDelta, footerVisible]);

  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );
  const { onThreadOrderProcessed, onThreadOrderProcessing } =
    useThreadOrderEventsContext();
  const [pendingReplyThreadId, setPendingReplyThreadId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!isEmailThreadsEnabled) {
      return;
    }
    return onThreadOrderProcessing((event) => {
      if (
        orderMetadata.some((o) => o.threadId && o.threadId === event.threadId)
      ) {
        setPendingReplyThreadId(event.threadId);
      }
    });
  }, [onThreadOrderProcessing, isEmailThreadsEnabled, orderMetadata]);

  useEffect(() => {
    if (!isEmailThreadsEnabled) {
      return;
    }
    return onThreadOrderProcessed((event) => {
      if (
        orderMetadata.some((o) => o.threadId && o.threadId === event.threadId)
      ) {
        setPendingReplyThreadId(null);
      }
    });
  }, [onThreadOrderProcessed, isEmailThreadsEnabled, orderMetadata]);

  const footerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: withTiming(
            footerVisible.value === 1 ? 0 : FOOTER_ACTIONS_HIDE_HEIGHT,
            {
              duration:
                footerVisible.value === 1
                  ? FOOTER_SLIDE_UP_ANIMATION_SPEED
                  : FOOTER_SLIDE_DOWN_ANIMATION_SPEED,
              easing: Easing.linear,
            },
          ),
        },
      ],
    };
  });

  // MEMOIZED VALUES //
  const hasTranslation = useMemo(() => {
    return orderMetadata.some((order) => hasTranslations(order));
  }, [orderMetadata]);

  const orderAvailability = useMemo(() => {
    const timezoneMap = {
      restaurant: selectedRestaurantRelationship.timezone,
      supplier: supplierTimezone,
    };
    const requestedFulfillmentType = orderMetadata?.find(
      (order) => order.fulfillmentType,
    )?.fulfillmentType as FulfillmentName | null;

    // The order agent can put a fulfillment type on the smart order that this
    // chat doesn't offer (it defaults to DELIVERY). Treat it as a candidate
    // only, so we don't end up displaying and validating one type while
    // writing another onto the order. An empty list means availability hasn't
    // loaded, not that nothing is offered, so leave the candidate alone.
    const isUnofferedType =
      availableOrderFulfillments.length > 0 &&
      !availableOrderFulfillments.some(
        (a) =>
          !a.primary_fulfillment_settings_id &&
          a.fulfillment_name === requestedFulfillmentType,
      );

    return new OrderAvailability(
      availableOrderFulfillments,
      timezoneMap,
      isUnofferedType ? null : requestedFulfillmentType || null,
    );
  }, [
    availableOrderFulfillments,
    orderMetadata,
    selectedRestaurantRelationship.timezone,
    supplierTimezone,
  ]);

  const numItemsInOrder = useMemo<number>(() => {
    return Object.keys(orderInboundState.inputToQuantityMap).reduce<number>(
      (acc, inputUUID) => {
        const { quantity, variantPackUUID } =
          orderInboundState.inputToQuantityMap[inputUUID];
        if (quantity !== 0 && !getAddToOrderDisabled(variantPackUUID)) {
          return acc + 1;
        }
        return acc;
      },
      0,
    );
  }, [orderInboundState.inputToQuantityMap, getAddToOrderDisabled]);

  const restaurantName = activeRestaurant
    ? activeRestaurant.name
    : isProcessing
      ? i18n.t('processing_account')
      : undefined;

  const TranslationToggle = useMemo(
    () =>
      hasTranslation ? (
        <TouchableOpacity
          onPress={() =>
            setShowTranslation((prev) => {
              Analytics.track('order_inbound_detail.show_translation_toggle', {
                nextShowTranslationState: !prev,
              });
              return !prev;
            })
          }
          style={styles.header}
        >
          <View style={styles.translationContainer}>
            <TranslationIcon fill={theme.sk.color.text.primary} />
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.h4SemiBold, styles.textPrimary]}
            >
              {showTranslation
                ? i18n.t('see_original')
                : i18n.t('see_translation')}
            </Text>
          </View>
        </TouchableOpacity>
      ) : null,
    [styles, theme, hasTranslation, showTranslation],
  );

  const originalInboundContent = useMemo(() => {
    return (
      <OrderInboundsDetailTabs
        onAreaSelect={(area) => setSelectedInputUUID(area.id)}
        onHighlightElementSelect={(searchInputUUID) =>
          setSelectedInputUUID(searchInputUUID)
        }
        orderMetadata={orderMetadata}
        selectedAreaID={selectedInputUUID ?? ''}
        showTranslations={showTranslation}
        translationToggle={TranslationToggle}
      />
    );
  }, [
    orderMetadata,
    selectedInputUUID,
    setSelectedInputUUID,
    showTranslation,
    TranslationToggle,
  ]);

  // NON-MEMOIZED VALUES //
  /*
   * When the bottom sheet is collapsed, it's not displaying content below the
   * first snap point. In this case, 50% - 85% of the screen.
   * Add this padding to the bottom of the list, with additional space to render
   * the continue button and its container
   */
  const defaultPaddingBottom = windowDimensions.height * 0.35;
  const detailPaddingBottom = defaultPaddingBottom + CONTINUE_BUTTON_HEIGHT;
  const continueButtonOffsetStyle =
    currentSnapPointIndex === 0 ? { paddingBottom: detailPaddingBottom } : null;

  const unknownContactOffsetStyle =
    currentSnapPointIndex === 0
      ? { paddingBottom: defaultPaddingBottom }
      : null;

  const unknownContactContent = unknownContactInfo ? (
    <View
      style={[
        styles.unknownContactContent,
        isSmallLayout ? unknownContactOffsetStyle : null,
      ]}
    >
      <InboundUnknownContact
        createdAt={orderMetadata[0].fullDate}
        initialActiveRestaurant={activeRestaurant}
        isBottomSheetExpanded={currentSnapPointIndex > 0}
        onUnknownContactSave={onUnknownContactSave}
        paddingHorizontal={SCREEN_PADDING_SMALL}
        unknownContactInfo={unknownContactInfo}
      />
    </View>
  ) : null;

  const processingContent = (
    <View
      style={[
        styles.processingContent,
        isSmallLayout
          ? styles.processingContentSmall
          : styles.processingContentLarge,
      ]}
    >
      {!isSmallLayout ? (
        <InboundDetailListCustomerAccountPicker
          containerStyle={
            styles.processingContentCustomerAccountPickerContainer
          }
          disabled={isProcessing}
          restaurantAddress={activeRestaurant?.addressString}
          restaurantName={restaurantName}
          showLocationPicker={showLocationPicker}
        />
      ) : null}

      <View
        style={[
          isSmallLayout ? null : styles.processingContentMessageContainer,
        ]}
      >
        <Text style={[TYPOGRAPHY.medium, styles.processingTitle]}>
          {i18n.t('smart_order.processing_title')}
        </Text>

        <Text style={[TYPOGRAPHY.small, styles.processingDescription]}>
          {i18n.t('smart_order.processing_description')}
        </Text>
      </View>
    </View>
  );

  const inboundDetailForm = (
    <InboundDetailForm
      activeRestaurant={activeRestaurant}
      addItemSmartOrderUUID={addItemSmartOrderUUID}
      alternativeVariantPacksMap={alternativeVariantPacksMap}
      containerStyle={[
        isSmallLayout
          ? [listContentContainerSmallStyle, continueButtonOffsetStyle]
          : null,
        isMediumLayout ? [styles.listContentContainerSmall] : null,
      ]}
      correctionPreviewMap={correctionPreviewMap}
      dispatch={dispatch}
      inDraggableSheet={isSmallLayout}
      isProcessing={isProcessing}
      isProcessingReply={isProcessingReply}
      listItems={detailListItems}
      matchTerms={orderInboundState.matchTerms}
      onInteraction={recordInteraction}
      onItemCorrection={onItemCorrection}
      onListScroll={(_, scrollDelta) => {
        setScrollDelta(scrollDelta);
      }}
      onRemoveItem={onRemoveItem}
      onSetRememberSelection={onSetRememberSelection}
      onSwapItem={onSwapItem}
      orderInboundState={orderInboundState}
      orderMetadata={orderMetadata}
      orderOptionsRef={orderOptionsRef}
      orderUUIDs={orderUUIDs}
      pillNavProps={pillNavProps}
      previousVariantPacksMap={previousVariantPacksMap}
      rehydrateKey={rehydrateKey}
      savedVariantPackOrderCountsMap={savedVariantPackOrderCountsMap}
      searchInputSubstitutionsMap={searchInputSubstitutionsMap}
      searchInputToQuantityMap={orderInboundState.inputToQuantityMap}
      showItemListView={showItemListView}
      showLocationPicker={showLocationPicker}
      showTranslation={showTranslation}
      workflowMode={workflowMode}
    />
  );
  const listContent = isSmallTabletLayout ? (
    <View
      style={{
        height: windowDimensions.height - 76 - DRAWER_HEADER_HEIGHT,
      }}
    >
      {inboundDetailForm}
    </View>
  ) : (
    inboundDetailForm
  );

  const hasExistingOrderItems = orderMetadata.some(
    (order) => order.searchInputs.length > 0,
  );
  const detailContent =
    isProcessing &&
    detailListItems.length === 0 &&
    (!hasExistingOrderItems || !isProcessingReply)
      ? processingContent
      : isUnknownContact
        ? unknownContactContent
        : listContent;

  // SIDE EFFECTS //
  useEffect(() => {
    // only run when we have a hydrated new order (actions) context for the
    // restaurant connected to the inbound
    if (
      !hasResponse ||
      isLoading ||
      !fulfillmentHydrated ||
      contextRestaurantUUID !== activeRestaurant?.uuid
    ) {
      return;
    }
    const requestedFulfillmentDate = orderMetadata?.find(
      (order) => order.fulfillmentAt,
    )?.fulfillmentAt;

    let targetFulfillment: SelectedFulfillmentType = {
      date: orderAvailability.nextFulfillmentDayMoment.toISOString(),
      fulfillmentSettingsUUID:
        orderAvailability.selectedFulfillmentSettingsUUID,
      timeslot: null,
      type: orderAvailability.fulfillmentType,
    };

    // Update to requested fulfillment date, if valid. Fall back to next available
    if (requestedFulfillmentDate) {
      const restaurantTimezone = selectedRestaurantRelationship.timezone;
      const resultMoment = adjustMomentForTimezone(
        requestedFulfillmentDate,
        restaurantTimezone,
      );

      const isValid = orderAvailability.isValidSupplierFulfillmentDate(
        resultMoment,
        supplierEmployeeRole,
        additionalOrderValidationRolesConfig,
      );

      if (isValid) {
        targetFulfillment = {
          date: resultMoment.toISOString(),
          fulfillmentSettingsUUID:
            orderAvailability.selectedFulfillmentSettingsUUID,
          timeslot: null,
          type: orderAvailability.fulfillmentType,
        };
      }
    }

    // Only update if target fulfillment is different from the last one set
    const currentKey = selectedFulfillmentRef.current
      ? `${selectedFulfillmentRef.current.date}-${selectedFulfillmentRef.current.fulfillmentSettingsUUID}`
      : '';
    const targetKey = `${targetFulfillment.date}-${targetFulfillment.fulfillmentSettingsUUID}`;

    if (currentKey !== targetKey) {
      setNextFulfillment(targetFulfillment);
      selectedFulfillmentRef.current = targetFulfillment;
    }
  }, [
    hasResponse,
    isLoading,
    orderAvailability,
    orderMetadata,
    selectedFulfillment,
    selectedRestaurantRelationship.timezone,
    setNextFulfillment,
    fulfillmentHydrated,
    contextRestaurantUUID,
    activeRestaurant?.uuid,
    additionalOrderValidationRolesConfig,
    supplierEmployeeRole,
  ]);

  useEffect(() => {
    if (orderInboundState.quantitiesAdjusted) {
      showUniqueToast({
        content: i18n.t('toast.quantity_adjustment'),
        type: Type.WARNING,
      });
    }
  }, [orderInboundState.quantitiesAdjusted, showUniqueToast]);

  useEffect(() => {
    if (markedAsReadRef.current || isProcessing) {
      return;
    }
    markedAsReadRef.current = true;
    markSmartOrdersAsRead(orderUUIDs);
  }, [markSmartOrdersAsRead, orderUUIDs, isProcessing]);

  const continueAction = isCorrectionMode ? null : trainingModeEnabled ? (
    <Button
      disabled={
        !hasResponse || isLoading || numItemsInOrder === 0 || isUnknownContact
      }
      loading={isMemorizingOrder}
      onPress={onUpdateMatchesPress}
      style={styles.continueButton}
      trackingData={{
        name: 'order_inbound_update_matches_button',
        properties: {
          orderUUIDs,
          restaurantUUID: activeRestaurant?.uuid,
          supplierUUID: activeRestaurant?.supplierUUID,
        },
      }}
    >
      {i18n.t('update_matches')}
    </Button>
  ) : !isViewOnly ? (
    <Button
      disabled={
        !hasResponse || isLoading || numItemsInOrder === 0 || isUnknownContact
      }
      loading={isUpdatingQuantity || continueButtonLoading}
      onPress={onContinuePress}
      style={styles.continueButton}
      trackingData={{
        name: 'order_inbound_submit_button',
        properties: {
          orderUUIDs,
          restaurantUUID: activeRestaurant?.uuid,
          supplierUUID: activeRestaurant?.supplierUUID,
        },
      }}
    >
      {i18n.t(
        numItemsInOrder === 1
          ? 'continue_count_singular'
          : 'continue_count_plural',
        {
          count: numItemsInOrder,
        },
      )}
    </Button>
  ) : null;

  const shouldShowHeader =
    isLargeLayout || isSmallTabletLayout || isMediumLayout;

  const shouldHideFooterButtons =
    isLargeLayout ||
    unknownContactInfo ||
    isProcessing ||
    hideMobileButtons ||
    isSmallTabletLayout ||
    isMediumLayout;

  const InboundDetailHeaderLeftSection: ReactNode = useMemo(() => {
    if (isSmallTabletLayout) {
      return !isInboundsListExpanded ? (
        <TouchableOpacity
          hitSlop={ICON_BUTTON_HIT_SLOP}
          onPress={() => setOrderInboundsListDrawerOpen?.(true)}
          style={styles.expandIcon}
          testID="expand-inbounds-list-panel"
        >
          <HamburgerIcon fill={theme.sk.color.icon.tertiary} />
        </TouchableOpacity>
      ) : null;
    }

    return !isInboundsListExpanded ? (
      <InboundListExpandButton
        onPress={() => setIsInboundsListExpanded(true)}
        style={styles.expandIcon}
      />
    ) : null;
  }, [
    isSmallTabletLayout,
    isInboundsListExpanded,
    styles.expandIcon,
    setOrderInboundsListDrawerOpen,
    setIsInboundsListExpanded,
    theme.sk.color.icon.tertiary,
  ]);

  return (
    <View style={[styles.flex, baseStyle]}>
      {isSmallLayout ? (
        <>
          <View style={styles.contentWrapperSmall}>
            <View
              onStartShouldSetResponderCapture={() => {
                recordInteraction();
                return false;
              }}
              style={styles.flex}
            >
              {originalInboundContent}
            </View>
          </View>
          <View style={styles.flex} />
          <BottomSheet
            enableContentPanningGesture={false}
            handleComponent={AffordanceHandleIndicatorWithSpacing}
            keyboardBehavior="extend"
            keyboardBlurBehavior="restore"
            onChange={setCurrentSnapPointIndex}
            ref={sheetRef}
            snapPoints={snapPoints}
            style={styles.bottomSheet}
          >
            {detailContent}
          </BottomSheet>
        </>
      ) : (
        <>
          {shouldShowHeader ? (
            <InboundDetailHeader
              action={!isProcessing ? continueAction : null}
              isProcessing={isProcessing}
              isUnknownContact={isUnknownContact}
              leftSection={InboundDetailHeaderLeftSection}
              onAddNewPress={() => setShowAddItemSheet(true)}
              orders={orderMetadata}
              workflowMode={workflowMode}
            />
          ) : null}
          <InboundDetailSplitContainer
            detailContent={detailContent}
            onResize={onResize}
            originalInboundContent={
              <View
                onStartShouldSetResponderCapture={() => {
                  recordInteraction();
                  return false;
                }}
                style={styles.flex}
              >
                {children}
                {originalInboundContent}
              </View>
            }
          />
        </>
      )}

      <OrderInboundsDetailActionSheet
        activeRestaurant={activeRestaurant}
        isSmallLayout={isSmallLayout}
        marginTop={marginTop}
        maxHeight={maxHeight}
        onClose={() => {
          setUpdatingLocation(false);
        }}
        onSelectLocation={onSelectLocation}
        orderUUIDs={orderUUIDs}
        setAssigningRestaurant={setAssigningRestaurant}
        unknownContactInfo={unknownContactInfo}
      />

      {(isSmallLayout || isSmallTabletLayout) &&
      Boolean(pendingReplyThreadId) ? (
        <Animated.View
          pointerEvents="box-none"
          style={[
            styles.mobileToastContainer,
            { bottom: Math.max(bottom, SCREEN_PADDING) + BUTTON_HEIGHT + 16 },
            footerAnimatedStyle,
          ]}
        >
          <Toast
            onClose={() => setPendingReplyThreadId(null)}
            options={{
              containerStyle: styles.mobileToastInnerContainer,
              content: (
                <View style={styles.mobileToastContent}>
                  <AlertCircleIcon
                    fill={theme.sk.color.icon.warning.primary}
                    height={16}
                    width={16}
                  />
                  <Typography
                    color={theme.sk.color.text.warning}
                    variant="paragraph-md"
                  >
                    {i18n.t('toast.email_thread.processing_new_reply')}
                  </Typography>
                </View>
              ),
              showCloseButton: false,
              type: Type.WARNING,
            }}
          />
        </Animated.View>
      ) : null}

      {shouldHideFooterButtons ? null : (
        <Animated.View
          style={[
            styles.footerActions,
            isSmallLayout || isSmallTabletLayout
              ? [
                  styles.footerActionsSmall,
                  {
                    bottom: Math.max(bottom, SCREEN_PADDING),
                  },
                ]
              : null,
            footerAnimatedStyle,
          ]}
        >
          <View
            style={
              isSmallLayout || isSmallTabletLayout
                ? styles.footerActionSmall
                : null
            }
          >
            {isViewOnly || isCorrectionMode ? null : (
              <Button
                leftElement={
                  <CircledIcon
                    Icon={PlusIcon}
                    iconHeight={20}
                    iconWidth={20}
                    size="small"
                  />
                }
                leftElementStyle={styles.addItemButtonIcon}
                onPress={navigateToAdd}
                style={styles.addItemButton}
                testID="order-inbound-add-button"
                trackingData={{
                  name: 'order_inbound_detail.add_item_button',
                  properties: {
                    orderUUIDs,
                  },
                }}
                type="secondary"
              >
                {i18n.t('add_item')}
              </Button>
            )}
          </View>
          <View
            style={
              isSmallLayout || isSmallTabletLayout
                ? styles.footerActionSmall
                : null
            }
          >
            {continueAction}
          </View>
        </Animated.View>
      )}
    </View>
  );
};

const BUTTON_HEIGHT = 56;
const BUTTON_RADIUS = 8;
const useStyles = createStyles((theme) => ({
  addItemButton: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.button.secondary.default,
    borderRadius: BUTTON_RADIUS,
    borderStyle: 'solid',
    borderWidth: 1,
    height: BUTTON_HEIGHT,
  },
  addItemButtonIcon: {
    marginRight: 8,
  },
  baseTablet: {
    backgroundColor: theme.colors.white,
  },
  bottomSheet: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: BOTTOM_SHEET_BORDER_RADIUS,
    borderTopRightRadius: BOTTOM_SHEET_BORDER_RADIUS,
    ...shadow({
      color: theme.colors.black,
      elevation: 10,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: -1,
    }),
  },
  contentWrapperSmall: {
    flex: 1,
  },
  continueButton: {
    borderRadius: BUTTON_RADIUS,
    height: BUTTON_HEIGHT,
  },
  expandIcon: {
    alignSelf: 'flex-start',
    marginRight: 8,
  },
  flex: {
    flex: 1,
  },
  footerActions: {
    alignItems: 'stretch',
    bottom: SCREEN_PADDING,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    position: 'absolute',
    right: SCREEN_PADDING,
  },
  footerActionSmall: {
    flexGrow: 1,
  },
  footerActionsSmall: {
    left: SCREEN_PADDING_XS,
    right: SCREEN_PADDING_XS,
  },
  header: {
    gap: SCREEN_PADDING_SMALL,
    marginTop: SCREEN_PADDING_SMALL,
  },
  listContentContainerSmall: {
    paddingBottom: 92,
  },
  listContentContainerSmallWeb: {
    paddingBottom: 56,
  },
  mobileToastContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
  },
  mobileToastContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  mobileToastInnerContainer: {
    backgroundColor: theme.sk.color.bg.warning,
    borderRadius: theme.sk.radii.radiusMedium,
    width: 343,
  },
  processingContent: {
    flex: 1,
    margin: SCREEN_PADDING,
  },
  processingContentCustomerAccountPickerContainer: {
    width: '100%',
  },
  processingContentLarge: {
    justifyContent: 'center',
  },
  processingContentMessageContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  processingContentSmall: {
    marginTop: SCREEN_PADDING,
  },
  processingDescription: {
    textAlign: 'center',
  },
  processingTitle: {
    ...FONTS.primarySemiBold,
    marginBottom: 6,
    textAlign: 'center',
  },
  textPrimary: {
    color: theme.sk.color.text.primary,
  },
  translationContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  unknownContactContent: {
    flex: 1,
    paddingTop: SCREEN_PADDING_SMALL,
  },
}));
